import type { AxiosInstance } from 'axios';

import Vue from 'vue';

import { formatPhone } from '@/utils/phone';

import { useApi } from '@/api/composable';
import { useProfileStore } from '@/store/profile';

export interface UserAccount {
  name: string
  avatarLink?: string
  phone: string
  isInvestor: boolean
  companyId: string
  sessionId?: string
  withoutAuth?: boolean
};

export const useAccountsStore = defineStore('users', () => {
  const profileStore = useProfileStore();
  const http = Vue.http as AxiosInstance;

  const {
    data,
    isLoading,
    execute,
    error,
  } = useApi(
    http,
    'admin/users/account/get_logins',
    { method: 'GET', localErrorHandle: true }
  );

  const rawList = computed<UserAccount[]>(() => {
    if (Array.isArray(data.value?.response)) {
      return data.value.response.map((r: Record<string, unknown>) => ({
        name: r.name,
        avatarLink: r['user-avatar'],
        phone: formatPhone(r.login as string),
        isInvestor: (r.type as string).includes('invest'),
        companyId: r.comp_id,
        sessionId: r.dev_sesh_id,
      } as UserAccount));
    }
    return [];
  });

  const list = computed(() => {
    const currentUserCompanies = profileStore.companiesList
      .filter((r: Record<string, unknown>) => !!r.taskmanager_task_id)
      .map((r: Record<string, unknown>) => ({
        name: r.name,
        avatarLink: (r.docs as Record<string, unknown>[]).find((doc) => doc.type === 'user-avatar')?.link,
        phone: profileStore.phone ? formatPhone(profileStore.phone) : '\u2014',
        isInvestor: (r.type as string).includes('invest'),
        companyId: r.id,
        withoutAuth: true,
      } as UserAccount));

    const otherCompanies = rawList.value
      .filter((c) => !profileStore.companiesList.find((r) => c.companyId === r.id));

    return [...currentUserCompanies, ...otherCompanies];
  });

  return {
    rawList,
    list,
    isLoading,
    error,

    init: execute,
    update: execute,
    execute,
  };
});
